import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { api } from '@/lib/api';
import { usePlans } from '@/lib/queries';
import { money } from '@/lib/format';
import { ErrorNote } from '@/components/Layout';
import { settleError, SettleForm, toPence, type SettleValues } from './Settle';

interface Line { person_id: string; name: string; plan_code: string; starts_on: string; ends_on: string; price_pence: number }
interface Suggestion { lines: Line[]; total_pence: number; family: boolean; individual_total_pence: number }

/**
 * A membership paid outside the portal: cash, bank transfer, handed over by an instructor, or free of charge.
 * Plans, dates and prices are suggested with the same rules as online checkout, and can be changed.
 */
export function RecordMembershipPayment({ people, onDone, onCancel }: { people: { id: string; first_name: string; last_name: string; frozen_at: string | null }[]; onDone: () => void; onCancel: () => void }) {
  const plans = usePlans();
  const eligible = people.filter(p => !p.frozen_at);
  const [picked, setPicked] = useState<string[]>([]);
  const [lines, setLines] = useState<Line[]>([]);
  const [family, setFamily] = useState(false);
  const [error, setError] = useState('');
  const [paymentId, setPaymentId] = useState<string | null>(null);
  const [receipt, setReceipt] = useState('');

  useEffect(() => {
    if (!picked.length) { setLines([]); return; }
    let live = true;
    supabase.rpc('suggest_membership_lines', { p_people: picked }).then(({ data, error }) => {
      if (!live) return;
      if (error) { setError(settleError(error.message)); return; }
      const q = data as Suggestion;
      setLines(q.lines); setFamily(q.family); setError('');
    });
    return () => { live = false; };
  }, [picked.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  const total = lines.reduce((s, l) => s + l.price_pence, 0);
  const up = (i: number, patch: Partial<Line>) => setLines(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));

  async function save(v: SettleValues): Promise<string | null> {
    const discount = v.provider === 'waived' ? 0 : toPence(v.discount);
    const { data, error } = await supabase.rpc('admin_record_membership_payment', {
      p_lines: lines.map(({ person_id, plan_code, starts_on, ends_on, price_pence }) => ({ person_id, plan_code, starts_on, ends_on, price_pence })),
      p_provider: v.provider, p_amount_pence: v.provider === 'waived' ? 0 : total - discount, p_received_on: v.received_on,
      p_reference: v.reference.trim() || null, p_note: v.note.trim() || null, p_collected_by: v.provider === 'instructor' ? v.collected_by : null,
      p_discount_pence: discount, p_discount_reason: discount ? v.discount_reason.trim() : null,
    });
    if (error) return settleError(error.message);
    setPaymentId(data as string);
    onDone();
    return null;
  }

  if (paymentId) return (
    <div className="flex flex-wrap items-center gap-3 rounded-sm bg-stone-50 p-4 text-[15px]">
      <span>Membership recorded and active.</span>
      {receipt ? <span className="text-ink-500">{receipt}</span> : <button className="btn-ghost border border-stone-300 text-sm" onClick={async () => {
        setReceipt('Sending…');
        try { await api('/admin/payments/receipt', { method: 'POST', body: { payment_id: paymentId } }); setReceipt('Receipt sent.'); } catch { setReceipt('Could not send the receipt.'); }
      }}>Email the receipt</button>}
      <button className="btn-ghost text-sm" onClick={onCancel}>Close</button>
    </div>
  );

  return (
    <div className="space-y-3 rounded-sm border border-stone-200 bg-white p-4">
      <p className="font-semibold">Record offline membership payment</p>
      <div className="flex flex-wrap gap-4 text-sm">
        {people.map(p => (
          <label key={p.id} className={`flex items-center gap-2 ${p.frozen_at ? 'text-ink-400' : ''}`}>
            <input type="checkbox" className="accent-brand-600" disabled={!!p.frozen_at} checked={picked.includes(p.id)} onChange={e => setPicked(e.target.checked ? [...picked, p.id] : picked.filter(x => x !== p.id))} />
            {p.first_name} {p.last_name}{p.frozen_at && ' (paused)'}
          </label>
        ))}
        {!eligible.length && <span className="text-ink-500">Everyone here has a paused membership.</span>}
      </div>
      {lines.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-ink-500"><tr><th className="py-1 pr-3 font-medium">Who</th><th className="pr-3 font-medium">Plan</th><th className="pr-3 font-medium">From</th><th className="pr-3 font-medium">To</th><th className="font-medium">Price £</th></tr></thead>
            <tbody className="divide-y divide-stone-100">{lines.map((l, i) => (
              <tr key={l.person_id}>
                <td className="py-1.5 pr-3">{l.name}</td>
                <td className="pr-3"><select className="input py-1" value={l.plan_code} onChange={e => { const p = plans.data?.find(x => x.code === e.target.value); up(i, { plan_code: e.target.value, price_pence: p?.price_pence ?? l.price_pence }); }}>
                  {(plans.data ?? []).map(p => <option key={p.code} value={p.code}>{p.name}</option>)}</select></td>
                <td className="pr-3"><input type="date" className="input py-1" value={l.starts_on} onChange={e => up(i, { starts_on: e.target.value })} /></td>
                <td className="pr-3"><input type="date" className="input py-1" value={l.ends_on} onChange={e => up(i, { ends_on: e.target.value })} /></td>
                <td><input className="input w-24 py-1" inputMode="decimal" value={(l.price_pence / 100).toFixed(2)} onChange={e => up(i, { price_pence: toPence(e.target.value.replace(/[^\d.]/g, '')) })} /></td>
              </tr>
            ))}</tbody>
          </table>
          <p className="mt-2 text-sm">Total <strong>{money(total)}</strong>{family && <span className="text-emerald-700"> · family price</span>}. If anyone renews by Direct Debit, their next collection moves on a year.</p>
        </div>
      )}
      {error && <ErrorNote>{error}</ErrorNote>}
      {lines.length > 0 && <SettleForm part="discount" title="Payment" count={lines.length} owedPence={total} onCancel={onCancel} onSave={save} />}
      {!lines.length && <button className="btn-ghost border border-stone-300 text-sm" onClick={onCancel}>Cancel</button>}
    </div>
  );
}
